import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Shepherd.js first-login product tour.
 *
 * Fires exactly ONCE per user (persisted in localStorage AND in
 * user_preferences.tour_completed) the first time the user lands on
 * /dashboard. Highlights: brand → workspace selector → notification
 * bell → attention cards → quick actions → Ask VAULT.
 *
 * All selectors below target existing `data-testid` attributes to
 * avoid coupling to styling. If a step's target isn't found, the tour
 * silently advances past it.
 */

const STEPS = [
    {
        id: "welcome",
        title: "Welcome to VAULT",
        text: "Take 30 seconds to see the four things you'll use every day. Skip anytime.",
        attachTo: null,
    },
    {
        id: "workspace",
        title: "Your workspace",
        text: "Switch between Personal, Family and Demo workspaces here. Everything else on the page updates instantly.",
        attachTo: '[data-testid="workspace-selector-btn"], [data-testid="workspace-selector-trigger"], [data-testid="workspace-selector"]',
        on: "bottom",
    },
    {
        id: "attention",
        title: "What needs attention today",
        text: "Overdue payments, cards due soon, statements missing OCR — anything that needs a decision shows up here.",
        attachTo: '[data-testid="concierge-attention"]',
        on: "bottom",
    },
    {
        id: "quick-actions",
        title: "Quick actions",
        text: "One tap to add an expense, upload a statement, record a payment or generate a CA pack.",
        attachTo: '[data-testid="concierge-quick-actions"]',
        on: "top",
    },
    {
        id: "bell",
        title: "Live notifications",
        text: "The bell shows anything time-sensitive — payment reminders, security events, statement OCR results.",
        attachTo: '[data-testid="topbar-alerts-btn"]',
        on: "bottom",
    },
    {
        id: "ask-vault",
        title: "Ask VAULT",
        text: "Your privacy-first AI assistant. Ask about your finances without your data ever leaving your workspace.",
        attachTo: '[data-testid="ask-vault-hero"], a[href="/copilot"]',
        on: "top",
    },
];

const STORAGE_KEY = "vault.product_tour_completed";

async function persistCompletion() {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    try {
        await api.patch("/user/preferences", { prefs: { tour_completed: true } });
    } catch { /* silent — localStorage is enough */ }
}

async function shouldRunTour() {
    // Localstorage short-circuit — 99% of the time this is the only check needed.
    try {
        if (localStorage.getItem(STORAGE_KEY) === "1") return false;
    } catch { /* noop */ }
    // Fallback: check the server prefs so a cleared localStorage doesn't nag again.
    try {
        const r = await api.get("/user/preferences");
        if (r.data?.tour_completed === true) {
            try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
            return false;
        }
    } catch { /* silent */ }
    return true;
}

export default function ProductTour() {
    const loc = useLocation();

    useEffect(() => {
        // Only on the dashboard / concierge root.
        if (!["/", "/dashboard"].includes(loc.pathname)) return;

        let cancelled = false;
        let tour = null;

        (async () => {
            if (!(await shouldRunTour())) return;
            if (cancelled) return;

            // Dynamic-import so shepherd.js only loads for first-time users.
            const [{ default: Shepherd }] = await Promise.all([
                import("shepherd.js"),
                import("shepherd.js/dist/css/shepherd.css"),
            ]);

            tour = new Shepherd.Tour({
                useModalOverlay: true,
                defaultStepOptions: {
                    classes: "vault-shepherd",
                    scrollTo: { behavior: "smooth", block: "center" },
                    cancelIcon: { enabled: true },
                    modalOverlayOpeningPadding: 6,
                    modalOverlayOpeningRadius: 12,
                },
            });

            const complete = () => { persistCompletion(); };
            tour.on("complete", complete);
            tour.on("cancel", complete);

            const total = STEPS.length;
            STEPS.forEach((s, idx) => {
                tour.addStep({
                    id: s.id,
                    title: s.title,
                    text: s.text,
                    attachTo: s.attachTo ? { element: s.attachTo, on: s.on || "bottom" } : undefined,
                    beforeShowPromise: () =>
                        new Promise((resolve) => {
                            if (!s.attachTo) return resolve();
                            // If the target isn't in the DOM, skip this step.
                            if (!document.querySelector(s.attachTo)) {
                                setTimeout(() => tour.next(), 0);
                                return resolve();
                            }
                            resolve();
                        }),
                    buttons: [
                        ...(idx > 0 ? [{
                            text: "Back",
                            secondary: true,
                            action() { return this.back(); },
                        }] : []),
                        {
                            text: idx === total - 1 ? "Finish" : "Next",
                            action() { return idx === total - 1 ? this.complete() : this.next(); },
                        },
                    ],
                });
            });

            // Wait a beat for the dashboard's initial API responses to hydrate DOM.
            setTimeout(() => {
                if (!cancelled) tour.start();
            }, 900);
        })();

        return () => {
            cancelled = true;
            try { tour?.complete(); } catch { /* noop */ }
        };
    }, [loc.pathname]);

    return null;
}
